import React, { useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { exportToExcelData } from '../../Common/PageHeader/helper';
import ComponentPageHeader from '../../Common/PageHeader/ComponentPageHeader';
import DataTable from '../../Common/DataTable';
import { VesselInformationRes, VesselInformationListingInterface } from './utils/type';
import { VesselInformationTableColumns } from './utils/helper';
import { resetIsVesselDeleted, deleteVesselStart, getVesselInformationStart } from './redux/sliceVesselInformation';
import { RootState } from '../../redux/rootState';
import { setLoader } from '../../redux/loaderSlice';
import { convertDateFormat } from '../../utils/CommonFunctions';
import {
  resetPagination, setPageNumber, setPageSize, setSearchParam,
  setSortColumn,
  setSortDirection,
} from '../taiReportingSystem/redux/sliceReportWorkspace';
import DeleteVesselConfirmationModal from '../../Common/DeleteVesselConfirmationModal/ComponentDeleteModal';

function VesselInformation() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tabId } = useParams();
  const [showConfirmationModal, setShowConfirmationModal] = useState('');
  // const [page, setPage] = useState(1);
  // const [rowsPerPage, setRowsPerPage] = useState(10);
  // const [searchQuery, setSearchQuery] = useState('');
  const {
    vesselInformation, totalVesselInfo, isLoading, isVesselDeleted,
  } = useSelector((state:RootState) => state.VesselInformationReducer);
  const { historyStack } = useSelector((state:RootState) => state.masterDataReducer);
  const {
    pageNumber, pageSize, searchParam, sortColumn, sortDirection,
  } = useSelector((state: RootState) => state.reportWorkspaceReducer);

  const handleData = () => {
    const payload = {
      page: pageNumber,
      rowsPerPage: pageSize,
      searchQuery: searchParam,
      sortCol: sortColumn,
      sortDir: sortDirection,
    };
    dispatch(getVesselInformationStart(payload));
  };

  useEffect(() => {
    dispatch(setLoader(isLoading));
  }, [isLoading]);

  const tableData: VesselInformationListingInterface[] = useMemo(() => {
    if (vesselInformation?.length > 0) {
      const transformedData = vesselInformation?.map((ele, index) => ({
        sn: index,
        action: 'edit&delete',
        jobOrder_infoTool: ele.JobOrder,
        vesselName: ele.VesselName,
        materialType: ele.MaterialType,
        testEnd: ele.TestEnd,
        tubeType: ele.TubeType,
        analyst: ele.Analyst,
        inspectionDate: convertDateFormat(ele.InspectionDate),
        infoText: ele.ParentJob || '',
        isJobCopied: ele.IsJobCopied,
        id: `${ele.JobOrder}-${ele.VesselId}`,
      }));
      return transformedData;
    }
    return [];
  }, [vesselInformation]);

  const handleEdit = (id: string) => {
    const record = vesselInformation.find((ele:VesselInformationRes) => `${ele.JobOrder}-${ele.VesselId}` === id);
    if (record) {
      navigate(tabId ? `${record.JobOrder}/${record.VesselId}/${tabId}` : `${record.JobOrder}/${record.VesselId}`);
    }
  };
  const handleDelete = (id: string) => {
    const record = vesselInformation.find((ele:VesselInformationRes) => `${ele.JobOrder}-${ele.VesselId}` === id);
    if (record) {
      const pay = {
        JobOrder: record.JobOrder,
        VesselId: String(record.VesselId),
      };
      dispatch(deleteVesselStart(pay));
    }
  };

  useEffect(() => {
    handleData();
  }, [pageNumber, pageSize, searchParam, sortColumn, sortDirection]);
  useEffect(() => {
    if (isVesselDeleted) {
      handleData();
      dispatch(resetIsVesselDeleted());
    }
  }, [isVesselDeleted]);

  const excelData = useMemo(() => vesselInformation?.map((item) => {
    const transformedDataItems = {
      JobOrder: item.JobOrder,
      VesselName: item.VesselName,
      MaterialType: item.MaterialType,
      TestEnd: item.TestEnd,
      TubeType: item.TubeType,
      Analyst: item.Analyst,
      InspectionDate: convertDateFormat(item.InspectionDate),
    };
    return transformedDataItems;
  }), [vesselInformation]);

  const downloadExcel = () => {
    exportToExcelData(
      {
        data: excelData,
      },
      'VESSEL INFORMATION',
    );
  };

  useEffect(() => {
    if (historyStack) {
      const finderString = 'vessel-information/';
      const last = historyStack[historyStack.length - 1];
      const sLast = historyStack[historyStack.length - 2];
      if (!(last?.includes(finderString) || (sLast?.includes(finderString) && last?.includes('vessel-information')))) {
        dispatch(resetPagination());
      }
    }
  }, [historyStack]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={12}>
        <ComponentPageHeader subHeading="VESSEL INFORMATION" downloadExcel={downloadExcel} />
      </Grid>
      <Grid item xs={12} sm={12} md={12} className="dataTableCustomMargin">
        <DataTable<VesselInformationListingInterface>
          data={tableData?.length > 0 ? tableData : []}
          columns={VesselInformationTableColumns}
          totalRecords={totalVesselInfo}
          page={pageNumber}
          setPage={(e) => { dispatch(setPageNumber(e)); }}
          rowsPerPage={pageSize}
          setRowsPerPage={(e) => { dispatch(setPageSize(e)); }}
          searchQuery={searchParam}
          setSearchQuery={(e) => { dispatch(setSearchParam(e)); dispatch(setPageNumber(1)); }}
          sortCol={sortColumn}
          setSortCol={(e) => { dispatch(setSortColumn(e)); dispatch(setPageNumber(1)); }}
          sortDir={sortDirection}
          setSortDir={(e) => { dispatch(setSortDirection(e)); dispatch(setPageNumber(1)); }}
          handleData={handleData}
          handleEdit={handleEdit}
          handleDelete={(id) => setShowConfirmationModal(id)}
          handleDataFromComponent
        />
      </Grid>
      <DeleteVesselConfirmationModal
        show={showConfirmationModal}
        setOpenFrom={() => setShowConfirmationModal('')}
        handleDelete={handleDelete}
      />
    </Grid>
  );
}

export default VesselInformation;
